import {
  Alert, Anchor, Badge, Card, Code, Group, SimpleGrid, Stack, Table, Text, Tooltip,
} from '@mantine/core';
import { IconArrowLeft, IconInfoCircle } from '@tabler/icons-react';
import { Link, useParams } from 'react-router-dom';

import { useDeliveries, useFunnel, useMessage } from '../api/hooks';
import { DataState } from '../components/DataState';
import { ErrorCodeBadge, StatusBadge } from '../components/StatusBadge';
import { PageHeader } from '../components/PageHeader';
import { CHANNEL_LABEL, fmtDate, fmtNumber } from '../theme/format';

export function MessageDetailPage() {
  const { id = '' } = useParams();
  const { data: message, isLoading, error } = useMessage(id, true);
  const { data: funnel } = useFunnel(id);
  const { data: deliveries } = useDeliveries(id);

  const isPush = message?.channel === 'push';

  return (
    <>
      <PageHeader
        title="Message"
        description={
          message
            ? `${CHANNEL_LABEL[message.channel] ?? message.channel} · ${message.source} · ${fmtDate(message.created_at)}`
            : undefined
        }
        action={
          <Anchor component={Link} to="/messages" size="sm">
            <Group gap={4}><IconArrowLeft size={14} /> All messages</Group>
          </Anchor>
        }
      />

      <DataState loading={isLoading} error={error}>
        <Stack gap="lg">
          {message?.error && (
            <Alert color={message.status === 'failed' ? 'red' : 'blue'} variant="light">
              {message.error}
            </Alert>
          )}

          <SimpleGrid cols={{ base: 2, sm: 4, lg: 6 }} spacing="sm">
            <Stat label="Recipients" value={fmtNumber(funnel?.total ?? message?.total)} />
            <Stat label="Sent" value={fmtNumber(funnel?.sent ?? message?.sent)} />
            {/*
              Accepted and delivered are shown as separate figures because for
              push only the first is ever populated. Collapsing them into one
              "delivered" number would be a lie the whole dashboard then repeats.
            */}
            <Stat
              label="Accepted"
              value={fmtNumber(funnel?.accepted)}
              help="What APNs, FCM, and web push actually report: they took the message."
            />
            <Stat
              label="Delivered"
              value={isPush ? 'n/a' : fmtNumber(funnel?.delivered)}
              help={
                isPush
                  ? 'Push has no delivery receipt. Neither APNs nor FCM will ever tell us the handset received it, so this stays empty rather than guessing.'
                  : 'Confirmed by the provider webhook.'
              }
            />
            <Stat label="Failed" value={fmtNumber(funnel?.failed ?? message?.failed)} />
            <Stat
              label="Skipped"
              value={fmtNumber(funnel?.skipped ?? message?.skipped)}
              help="Refused by a gate: suppressed, opted out, capped, or a dry run."
            />
          </SimpleGrid>

          <Card>
            <Group justify="space-between" mb="md">
              <Text fw={500}>Per-recipient delivery</Text>
              <Badge variant="light" color="gray">{deliveries?.length ?? 0} shown</Badge>
            </Group>

            <DataState
              empty={!deliveries?.length}
              emptyTitle="No delivery rows yet"
              emptyDescription="Fan-out happens in a worker moments after the send is accepted."
            >
              <Table.ScrollContainer minWidth={780}>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Address</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Reason</Table.Th>
                      <Table.Th ta="center">Attempts</Table.Th>
                      <Table.Th>Accepted</Table.Th>
                      <Table.Th>Delivered</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {deliveries?.map((d) => (
                      <Table.Tr key={d.id}>
                        <Table.Td>
                          {d.profile_id ? (
                            <Anchor component={Link} to={`/profiles/${d.profile_id}`} size="sm">
                              <Code>{d.address ?? '—'}</Code>
                            </Anchor>
                          ) : (
                            <Code>{d.address ?? '—'}</Code>
                          )}
                        </Table.Td>
                        <Table.Td><StatusBadge status={d.status} /></Table.Td>
                        <Table.Td>
                          <ErrorCodeBadge code={d.error_code} detail={d.error_detail} />
                        </Table.Td>
                        <Table.Td ta="center">
                          <Text size="sm" c={d.attempts > 1 ? 'yellow' : undefined}>
                            {d.attempts}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed">{fmtDate(d.accepted_at)}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed">
                            {isPush ? 'n/a' : fmtDate(d.delivered_at)}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </DataState>
          </Card>
        </Stack>
      </DataState>
    </>
  );
}

function Stat({ label, value, help }: { label: string; value: string; help?: string }) {
  return (
    <Card padding="sm">
      <Group gap={4}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.04em' }}>
          {label}
        </Text>
        {help && (
          <Tooltip label={help} multiline w={300} withArrow>
            <IconInfoCircle size={13} style={{ opacity: 0.5, cursor: 'help' }} />
          </Tooltip>
        )}
      </Group>
      <Text fz={22} fw={600} mt={2} lh={1.2}>{value}</Text>
    </Card>
  );
}
